import React, { useState, useEffect } from 'react';
import { notification } from '../../component/hocs/notification';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/utility/Page';
import DataTable from 'react-data-table-component';
import { custTableStyles } from '../../style/color';
import axios from 'axios';
import { mainurl } from '../../Mainurl';
import * as XLSX from 'xlsx/xlsx.mjs';

import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Form,
    Input,
    InputGroup,
    InputGroupText,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Badge,
    Spinner,
    Alert
} from 'reactstrap';
import {
    FaEye,
    FaSearch,
    FaDownload,
    FaPaw,
    FaUsers,
    FaChartBar,
    FaUser,
    FaClipboardList
} from 'react-icons/fa';

const LivestockCount = () => {
    const [mode, setMode] = useState("1");
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);

    const [viewModal, setViewModal] = useState(false);
    const [modalData, setModalData] = useState('');

    const get_livestock_list = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let postData = [{
                "org_id": sessionStorage.getItem('org_id'),
                "user_id": sessionStorage.getItem('user_id'),
                "lang_id": 1
            }];

            const response = await axios({
                method: 'post',
                url: mainurl + 'Activity/animal_count',
                data: postData,
                headers: { 'Content-Type': 'application/JSON', }
            });

            let list = response.data.map((row, index) => {
                return {
                    sl: index + 1,
                    farmer_id: row.farmer_id,
                    f_name: row.f_spouse_name,
                    animal_type_name: row.animal_type_name,
                    animal_count: row.animal_count,
                    action: (
                        <Button
                            color="info"
                            size="sm"
                            outline
                            className="d-flex align-items-center gap-1"
                            onClick={() => viewDetailsHandler(row)}
                        >
                            <FaEye size={14} />
                            View
                        </Button>
                    ),
                    rawData: row
                };
            });

            setTableData(list);
            setFilteredData(list);

            let notify = notification({
                message: `${list.length} livestock records loaded successfully`,
                type: 'success'
            });
            notify();
        } catch (error) {
            let notify = notification({
                message: "Failed to load livestock data. Please try again.",
                type: 'error'
            });
            notify();
        } finally {
            setLoading(false);
        }
    };

    const viewDetailsHandler = (data) => {
        setModalData(data);
        setViewModal(true);
    };

    const download_excel_handler = async () => {
        setDownloadLoading(true);

        try {
            if (tableData.length === 0) {
                let notify = notification({
                    message: "No data available for download",
                    type: 'warning'
                });
                notify();
                return;
            }

            let list = tableData.map((row, index) => {
                return {
                    sl_no: index + 1,
                    farmer_id: row.farmer_id,
                    farmer_name: row.f_name,
                    animal_type: row.animal_type_name,
                    animal_count: row.animal_count
                };
            });

            let wb = XLSX.utils.book_new(),
                ws = XLSX.utils.json_to_sheet(list);
            XLSX.utils.book_append_sheet(wb, ws, "Livestock_Report");
            XLSX.writeFile(wb, `livestock_report_${new Date().toISOString().split('T')[0]}.xlsx`);

            let notify = notification({
                message: "Excel report downloaded successfully!",
                type: 'success'
            });
            notify();
        } catch (error) {
            let notify = notification({
                message: "Download failed. Please try again.",
                type: 'error'
            });
            notify();
        } finally {
            setDownloadLoading(false);
        }
    };

    // Search functionality
    const handleSearch = (value) => {
        setSearchTerm(value);
        if (value === '') {
            setFilteredData(tableData);
        } else {
            const filtered = tableData.filter(item =>
                item.farmer_id.toLowerCase().includes(value.toLowerCase()) ||
                item.f_name.toLowerCase().includes(value.toLowerCase()) ||
                item.animal_type_name.toLowerCase().includes(value.toLowerCase()) ||
                item.animal_count.toString().includes(value)
            );
            setFilteredData(filtered);
        }
    };

    // Calculate statistics
    const totalAnimals = tableData.reduce((sum, item) => sum + parseInt(item.animal_count || 0), 0);
    const uniqueFarmers = new Set(tableData.map(item => item.farmer_id)).size;
    const uniqueAnimalTypes = new Set(tableData.map(item => item.animal_type_name)).size;
    const avgAnimalsPerFarmer = uniqueFarmers > 0 ? Math.round(totalAnimals / uniqueFarmers) : 0;

    const columns = [
        {
            name: '#',
            selector: row => row.sl,
            sortable: true,
            width: '70px',
            center: true
        },
        {
            name: (
                <div className="d-flex align-items-center gap-1">
                    <FaUser size={12} />
                    Farmer ID
                </div>
            ),
            selector: row => row.farmer_id,
            sortable: true,
            width: '120px',
            cell: row => (
                <Badge color="light" className="text-primary" pill>
                    {row.farmer_id}
                </Badge>
            )
        },
        {
            name: (
                <div className="d-flex align-items-center gap-1">
                    <FaUsers size={12} />
                    Farmer Name
                </div>
            ),
            selector: row => row.f_name,
            sortable: true,
            width: '30%',
            cell: row => (
                <div className="text-truncate" title={row.f_name}>
                    {row.f_name || 'N/A'}
                </div>
            )
        },
        {
            name: (
                <div className="d-flex align-items-center gap-1">
                    <FaPaw size={12} />
                    Animal Type
                </div>
            ),
            selector: row => row.animal_type_name,
            sortable: true,
            width: '20%',
            cell: row => (
                <Badge 
                    color={
                        row.animal_type_name?.toLowerCase().includes('cow') || 
                        row.animal_type_name?.toLowerCase().includes('cattle') ? 'success' :
                        row.animal_type_name?.toLowerCase().includes('goat') ? 'warning' :
                        row.animal_type_name?.toLowerCase().includes('chicken') || 
                        row.animal_type_name?.toLowerCase().includes('poultry') ? 'info' :
                        'secondary'
                    }
                    className="text-white"
                >
                    {row.animal_type_name}
                </Badge>
            )
        },
        {
            name: (
                <div className="d-flex align-items-center gap-1">
                    <FaChartBar size={12} />
                    Count
                </div>
            ),
            selector: row => row.animal_count,
            sortable: true,
            center: true,
            width: '100px',
            cell: row => (
                <Badge 
                    color={
                        parseInt(row.animal_count) > 50 ? 'success' : 
                        parseInt(row.animal_count) > 20 ? 'warning' : 
                        'danger'
                    } 
                    pill
                    className="fs-6"
                >
                    {row.animal_count}
                </Badge>
            )
        },
        {
            name: 'Actions',
            selector: row => row.action,
            sortable: false,
            center: true,
            width: '120px'
        }
    ];

    return (
        <>
            <Header />
            <Page title='Livestock Count Management' breadcrumbs='Home / Livestock Count'>
                <Row className='p-3'>
                    {mode === "1" && (
                        <Col xs={12}>
                            {/* Action Bar */}
                            <Card className='mb-4 border-0 shadow-sm'>
                                <CardBody className="py-3">
                                    <Row className="align-items-center">
                                        <Col md={8}>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    color='primary'
                                                    size="sm"
                                                    onClick={get_livestock_list}
                                                    disabled={loading}
                                                    className="d-flex align-items-center gap-2"
                                                >
                                                    {loading ? (
                                                        <Spinner size="sm" />
                                                    ) : (
                                                        <FaPaw size={14} />
                                                    )}
                                                    {loading ? 'Loading...' : 'Load Livestock Data'}
                                                </Button>
                                                <Button
                                                    color='secondary'
                                                    size="sm"
                                                    onClick={download_excel_handler}
                                                    disabled={downloadLoading || tableData.length === 0}
                                                    className="d-flex align-items-center gap-2 text-white"
                                                >
                                                    {downloadLoading ? (
                                                        <Spinner size="sm" />
                                                    ) : (
                                                        <FaDownload size={14} />
                                                    )}
                                                    {downloadLoading ? 'Downloading...' : 'Download Excel'}
                                                </Button>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <InputGroup size="sm">
                                                <InputGroupText className="bg-light border-end-0">
                                                    <FaSearch className="text-muted" />
                                                </InputGroupText>
                                                <Input
                                                    type="search"
                                                    placeholder='Search farmers, animals, types...'
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    className="border-start-0"
                                                />
                                            </InputGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* Statistics Cards */}
                            {tableData.length > 0 && (
                                <Row className="mb-4">
                                    <Col md={3}>
                                        <Card className="border-0 bg-primary text-white">
                                            <CardBody className="text-center py-3">
                                                <FaClipboardList size={24} className="mb-2" />
                                                <h5 className="mb-0">{tableData.length}</h5>
                                                <small>Total Records</small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 bg-success text-white">
                                            <CardBody className="text-center py-3">
                                                <FaPaw size={24} className="mb-2" />
                                                <h5 className="mb-0">{totalAnimals}</h5>
                                                <small>Total Animals</small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 bg-info text-white">
                                            <CardBody className="text-center py-3">
                                                <FaUsers size={24} className="mb-2" />
                                                <h5 className="mb-0">{uniqueFarmers}</h5>
                                                <small>Active Farmers</small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 bg-warning text-white">
                                            <CardBody className="text-center py-3">
                                                <FaChartBar size={24} className="mb-2" />
                                                <h5 className="mb-0">{avgAnimalsPerFarmer}</h5>
                                                <small>Avg. per Farmer</small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            )}

                            {/* Main Data Table */}
                            <Card className='border-0 shadow-sm'>
                                <CardHeader className="bg-white border-bottom">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h5 className="mb-0">Livestock Records</h5>
                                            <small className='text-muted'>
                                                {filteredData.length > 0 ?
                                                    `Showing ${filteredData.length} of ${tableData.length} records` :
                                                    'No livestock records found'
                                                }
                                            </small>
                                        </div>
                                        {searchTerm && (
                                            <Badge color="secondary" className="ms-2">
                                                Filtered: {filteredData.length}
                                            </Badge>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardBody className="p-0">
                                    {tableData.length === 0 && !loading ? (
                                        <Alert color="info" className="m-3 text-center">
                                            <FaPaw size={48} className="mb-3 text-muted" />
                                            <p className="mb-2">No livestock data loaded yet</p>
                                            <small>Click "Load Livestock Data" to fetch the latest records</small>
                                        </Alert>
                                    ) : (
                                        <DataTable
                                            title=""
                                            customStyles={custTableStyles}
                                            columns={columns}
                                            data={filteredData}
                                            highlightOnHover
                                            responsive
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            progressPending={loading}
                                            progressComponent={
                                                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                                    <Spinner color="primary" />
                                                    <p className="mt-2 text-muted">Loading livestock data...</p>
                                                </div>
                                            }
                                            noDataComponent={
                                                <div className="text-center py-5">
                                                    <FaSearch size={48} className="text-muted mb-3" />
                                                    <p className="text-muted">No records match your search criteria</p>
                                                </div>
                                            }
                                        />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                </Row>
            </Page>

            {/* Enhanced Modal for Details */}
            <Modal
                centered
                size="md"
                isOpen={viewModal}
                toggle={() => setViewModal(!viewModal)}
            >
                <ModalHeader
                    toggle={() => setViewModal(!viewModal)}
                    className="bg-light border-bottom"
                >
                    <div className="d-flex align-items-center gap-2">
                        <FaPaw className="text-primary" />
                        <div>
                            <h5 className="mb-0">Livestock Details</h5>
                            <small className="text-muted">
                                Farmer information and animal count
                            </small>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody>
                    {modalData && (
                        <div className="row g-3">
                            <div className="col-12">
                                <Card className="border-0 bg-light">
                                    <CardBody>
                                        <Row>
                                            <Col md={6} className="mb-3">
                                                <label className="form-label text-muted small">Farmer ID</label>
                                                <div className="fw-bold">{modalData.farmer_id}</div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <label className="form-label text-muted small">Farmer Name</label>
                                                <div className="fw-bold">{modalData.f_spouse_name || 'N/A'}</div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <label className="form-label text-muted small">Animal Type</label>
                                                <div>
                                                    <Badge 
                                                        color={
                                                            modalData.animal_type_name?.toLowerCase().includes('cow') || 
                                                            modalData.animal_type_name?.toLowerCase().includes('cattle') ? 'success' :
                                                            modalData.animal_type_name?.toLowerCase().includes('goat') ? 'warning' :
                                                            modalData.animal_type_name?.toLowerCase().includes('chicken') || 
                                                            modalData.animal_type_name?.toLowerCase().includes('poultry') ? 'info' :
                                                            'secondary'
                                                        }
                                                        className="text-white fs-6"
                                                    >
                                                        {modalData.animal_type_name}
                                                    </Badge>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <label className="form-label text-muted small">Animal Count</label>
                                                <div>
                                                    <Badge 
                                                        color={
                                                            parseInt(modalData.animal_count) > 50 ? 'success' : 
                                                            parseInt(modalData.animal_count) > 20 ? 'warning' : 
                                                            'danger'
                                                        } 
                                                        pill
                                                        className="fs-5"
                                                    >
                                                        {modalData.animal_count} Animals
                                                    </Badge>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </div>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            <Footer />
        </>
    );
};

export default LivestockCount;